import React from 'react';
import './index.scss';
import Proptypes from 'prop-types';
import cn from 'classnames';
import { Icon, AccessibilityWrapper } from 'legacy-packages/ft-cb';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import { connect } from 'react-redux';
import { removeHtmlFromText } from 'legacy/src/utility/utils';
import VerticalPerformanceDetails from 'legacy/src/components/assessment/Results/VerticalPerformanceDetails/index';
import { performanceTierStacks } from 'legacy/src/utility/progressHelpers';
import sortBy from 'lodash/sortBy';
import uniqid from 'uniqid';
import LearnosityText from 'legacy/src/components/learnosity/LearnosityText';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

const MCQOption = ({
  text,
  rationale,
  letter,
  ariaLabelText,
  isChosen,
  isCorrect,
  isActive,
  setActive,
  optionUsers,
  assignmentReport,
  subjectId,
  students,
  studentUrl,
  showPerformance,
  isMultipleResponse,
  showAnalysis,
  itemId,
  announceOptions,
}) => {
  const { performanceTiers } = usePerformanceTiers(subjectId);

  const stacks = performanceTierStacks(
    optionUsers.length,
    assignmentReport,
    performanceTiers,
    students,
    itemId,
  )
    .map((stack) => ({
      ...stack,
      students: sortBy(
        stack.students.filter((student) => optionUsers.includes(student.id)),
        ['lastName', 'firstName'],
      ),
    }))
    .filter((stack) => stack.students.length !== 0 && stack.type !== 'default')
    .reverse();

  const responseCounterAriaLabel = `Number of students selecting Option ${letter}: ${optionUsers.length}`;
  const popoverAriaLabel = stacks.reduce(
    (acc, stack) =>
      `${acc}
    ${stack.type} (${stack.label}):
    ${stack.students.reduce(
      (studentNames, student) =>
        `${studentNames} ${student.firstName} ${student.lastName}`,
      '',
    )}`,
    '',
  );
  const ariaDescribedBy = `option-details-${uniqid()}`;
  const activeStudentsResultCount = stacks.length
    ? stacks
        .map((stack) => stack.students.length)
        .reduce((prevValue, currentValue) => prevValue + currentValue, 0)
    : 0;

  const responseCounter = (
    <label className="cursor-pointer response-analysis">
      <div className="response-count">{activeStudentsResultCount}</div>
      <Icon name="person" size="1em" />
    </label>
  );

  const ariaLabel = `Option ${letter}: ${ariaLabelText} ${
    isActive ? `Rationale: ${removeHtmlFromText(rationale)}` : ''
  }`;

  return (
    <AccessibilityWrapper callback={setActive}>
      <div
        tabIndex={0}
        className={cn('mcq-option', {
          '--correct': isCorrect && isActive,
          '--incorrect': !isCorrect && isActive,
          'multiple-response': isMultipleResponse,
        })}
        onClick={setActive}
        role="button"
        {...(announceOptions ? { 'aria-live': 'polite' } : {})}
        aria-label={ariaLabel}
      >
        <div className={cn('letter', { '--chosen': isChosen })}>{letter}</div>
        <LearnosityText text={text} className="lrn text" />
        <div className="response-analysis-wrapper">
          <div
            className={cn('icon', {
              '--correct': isCorrect,
              '--incorrect': !isCorrect && isActive,
            })}
          />
          {showPerformance &&
            showAnalysis &&
            (activeStudentsResultCount ? (
              <PopOver
                showCloseButton
                className="popover"
                accessibility={{
                  'aria-label': responseCounterAriaLabel,
                  'aria-describedby': ariaDescribedBy,
                }}
                trigger={responseCounter}
              >
                <VerticalPerformanceDetails
                  ariaId={ariaDescribedBy}
                  aria-label={popoverAriaLabel}
                  studentUrl={studentUrl}
                  stacks={stacks}
                />
              </PopOver>
            ) : (
              <div className="response-counter">{responseCounter}</div>
            ))}
        </div>
      </div>
    </AccessibilityWrapper>
  );
};

MCQOption.propTypes = {
  text: Proptypes.string.isRequired,
  letter: Proptypes.string.isRequired,
  setActive: Proptypes.func.isRequired,
  optionUsers: Proptypes.array.isRequired,
  isCorrect: Proptypes.bool,
  isActive: Proptypes.bool,
  isMultipleResponse: Proptypes.bool,
  showAnalysis: Proptypes.bool,
  itemId: Proptypes.string,
  announceOptions: Proptypes.bool,
};

MCQOption.defaultProps = {
  isCorrect: false,
  isActive: false,
  isMultipleResponse: false,
  showAnalysis: true,
  itemId: null,
  announceOptions: true,
};

export default connect((state) => ({
  subjectId: state.subject.data.id,
}))(MCQOption);
